import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { enumLabel } from '../../lib/enumLabel';
import { formatDate, formatEUR } from '../../lib/format';
import { cn } from '../../utils/cn';

interface PaymentRow {
  id: string;
  at: string;
  method: string;
  amount: number;
  type: string;
  reference: string | null;
  reversalReason: string | null;
  isReversal: boolean;
  provisional: boolean | null;
  invoiceNumber: string | null;
  by: string | null;
}

/**
 * Every payment of the order (order-sheet prototype, "Payments"): date, method, amount — a
 * reversal reads negative with its reason — reference, who took it and the invoice it went to.
 * Straight from the payment ledger (GET /orders/:id/payments); the total is the ledger's sum.
 */
export function OrderPayments({ orderId, refreshKey }: { orderId: string; refreshKey: number }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PaymentRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    apiClient
      .get<{ data: PaymentRow[] }>(`/orders/${orderId}/payments`)
      .then((r) => {
        if (alive) setRows(r.data.data);
      })
      .catch(() => {
        if (alive) setRows([]);
      });
    return () => {
      alive = false;
    };
  }, [orderId, refreshKey]);

  if (!rows || rows.length === 0) return null;
  const total = Math.round(rows.reduce((s, r) => s + Number(r.amount), 0) * 100) / 100;

  return (
    <section className="border rounded p-4 bg-card">
      <h2 className="font-semibold mb-2">{t('orders.payments.title')}</h2>
      <ul className="divide-y text-sm">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-baseline gap-3 py-1.5">
            <span className="w-24 text-xs text-muted-foreground">{formatDate(r.at)}</span>
            <span className="w-28">{enumLabel('paymentMethod', r.method)}</span>
            <span className="flex-1 text-xs text-muted-foreground">
              {[
                r.isReversal && t('orders.payments.reversal', { reason: r.reversalReason ?? '' }),
                r.provisional && t('orders.payments.provisional'),
                r.reference,
                r.invoiceNumber && t('orders.payments.onInvoice', { number: r.invoiceNumber }),
                r.by && t('orders.payments.by', { name: r.by }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <span className={cn('tabular-nums font-medium', r.amount < 0 && 'text-destructive')}>
              {formatEUR(Number(r.amount))}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-1 flex justify-between border-t-2 pt-1 text-sm font-semibold">
        <span>{t('orders.payments.total')}</span>
        <span className="tabular-nums">{formatEUR(total)}</span>
      </div>
    </section>
  );
}
